import { useState, useLayoutEffect, useCallback, useEffect, useMemo } from "react";
import { ScrollView, Pressable, ActivityIndicator } from "react-native";
import { useRouter, useLocalSearchParams, useNavigation } from "expo-router";
import { View, Text, ScreenContainer, Switch, Banner } from "@src/components";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { DataStateView } from "@src/components/screen-state";
import { useAlarm, useUpdateAlarmResponse } from "@src/services/api/hooks";
import type { ArmedResponse } from "@src/services/api/types";
import {
  AlarmResponseFeatures,
  type AlarmFeatures,
  type AlarmResponseFeature,
} from "@visioni/a3s-alarm-schedules/schema";
import { showSuccessToast, showErrorToast } from "@src/utils/toast";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import { useTenantId } from "@src/features/tenants";
import { useUnistyles } from "react-native-unistyles";
import { isWeb } from "@src/utils/web-helpers";

export default function ResponseScreen() {
  const router = useRouter();
  const navigation = useNavigation();
  const { headerLeft } = useHeaderBackButton();
  const { track } = useAmplitude();
  const tenantId = useTenantId();
  const { theme } = useUnistyles();
  const { siteId, cameraKey, recording, monitoring, sirens, strobes } = useLocalSearchParams<{
    siteId: string;
    cameraKey: string;
    recording?: string;
    monitoring?: string;
    sirens?: string;
    strobes?: string;
  }>();
  const { data: alarm, isLoading: isLoadingAlarm, isFetching, error, refetch } = useAlarm(siteId, cameraKey);
  const updateResponseMutation = useUpdateAlarmResponse();

  // Local state for toggle switches
  const [response, setResponse] = useState<ArmedResponse>({
    recording: "on",
    monitoring: "on",
    sirens: "off",
    strobes: "off",
  });

  // Update local state when alarm data loads or URL params are provided (from agent)
  useEffect(() => {
    // Priority: URL params (from agent) > alarm data > defaults
    if (recording || monitoring || sirens || strobes) {
      setResponse({
        recording: (recording as "on" | "off") || "on",
        monitoring: (monitoring as "on" | "off") || "on",
        sirens: (sirens as "on" | "off") || "off",
        strobes: (strobes as "on" | "off") || "off",
      });
    } else if (alarm?.settings?.response) {
      setResponse(alarm.settings.response);
    }
  }, [alarm, recording, monitoring, sirens, strobes]);

  const handleSave = useCallback(() => {
    if (!cameraKey) return;

    updateResponseMutation.mutate(
      { siteId, cameraKey, response },
      {
        onSuccess: () => {
          track(ANALYTICS_EVENTS.ALARM_RESPONSE_SAVED, {
            camera_id: cameraKey,
            response,
            tenant_id: tenantId,
            site_id: siteId,
          });
          showSuccessToast({ message: "Response saved" });
          router.back();
        },
        onError: (error) => {
          console.error("Failed to update alarm response:", error);
          showErrorToast({ message: "Error", description: "Failed to update alarm response. Please try again." });
        },
      },
    );
  }, [cameraKey, tenantId, siteId, response, updateResponseMutation, router, track]);

  // Configure navigation header
  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Change Response",
      headerBackButtonDisplayMode: "minimal",
      headerLeft,
      headerRight: () => (
        <Pressable onPress={handleSave} style={{ paddingHorizontal: 16 }} disabled={updateResponseMutation.isPending}>
          {updateResponseMutation.isPending ?
            <ActivityIndicator size="small" />
          : <Text textStyle="md/semibold" color="navy">
              Save
            </Text>
          }
        </Pressable>
      ),
    });
  }, [navigation, headerLeft, handleSave, updateResponseMutation.isPending]);

  // Convert AlarmResponseFeatures object to array with id fields
  // Also include "recording" which is in ArmedResponse but not in AlarmResponseFeatures
  const alarmResponseFeaturesArray = useMemo<Array<AlarmResponseFeature & { id: AlarmFeatures }>>(
    () => [
      {
        id: "recording" as AlarmFeatures,
        defaultState: "on",
        allowedStates: ["on", "off"],
        title: "Recording",
        description: "Record video when the alarm is activated",
      },
      ...(AlarmResponseFeatures ?
        (Object.entries(AlarmResponseFeatures) as [AlarmFeatures, AlarmResponseFeature][]).map(([id, feature]) => ({
          ...feature,
          id,
        }))
      : []),
    ],
    [],
  );

  // Toggle handler for a specific feature
  const handleToggle = useCallback(
    (featureId: AlarmFeatures) => {
      track(ANALYTICS_EVENTS.ALARM_RESPONSE_EDITED, {
        camera_id: cameraKey,
        feature_id: featureId,
        tenant_id: tenantId,
        site_id: siteId,
      });
      setResponse((prev: ArmedResponse) => {
        const feature = alarmResponseFeaturesArray.find((f) => f.id === featureId);
        if (!feature) return prev;

        // If only one allowed state, don't toggle
        if (feature.allowedStates.length === 1) {
          return prev;
        }

        // Toggle between allowed states
        const currentValue = prev[featureId as keyof ArmedResponse] as "on" | "off";
        const currentIndex = feature.allowedStates.indexOf(currentValue);
        const nextIndex = (currentIndex + 1) % feature.allowedStates.length;
        const nextValue = feature.allowedStates[nextIndex] as "on" | "off";

        return {
          ...prev,
          [featureId]: nextValue,
        } as ArmedResponse;
      });
    },
    [cameraKey, tenantId, siteId, track, alarmResponseFeaturesArray],
  );

  // Render response content
  const renderResponseContent = () => (
    <ScrollView style={{ flex: 1 }}>
      {isWeb && (
        <View px="5xl" pt="4xl" pb="xl" bg="background" alignItems="flex-end">
          <Pressable onPress={handleSave} disabled={updateResponseMutation.isPending}>
            {updateResponseMutation.isPending ?
              <ActivityIndicator size="small" />
            : <Text textStyle="md/semibold" color="navy">
                Save
              </Text>
            }
          </Pressable>
        </View>
      )}

      {/* Info Banner */}
      <View px="5xl" pt="4xl" bg="background">
        <Banner
          variant="info"
          title="What is a response?"
          message={`The response controls what the system does when an alarm activates.\nEg: Turn on sirens and strobes.`}
        />
      </View>

      {/* Response Toggles */}
      <View px="5xl" py="xl" bg="background" gap="md">
        {alarmResponseFeaturesArray.map((feature) => {
          const isDisabled = feature.allowedStates.length === 1;
          const currentValue = response[feature.id as keyof ArmedResponse] as "on" | "off";
          const isActive = currentValue === "on";

          return (
            <Pressable key={feature.id} onPress={() => !isDisabled && handleToggle(feature.id)} disabled={isDisabled}>
              <View
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                bg="surface"
                borderWidth={1}
                borderColor="border"
                p="4xl"
                rounded={8}
                style={{ opacity: isDisabled ? 0.5 : 1 }}
              >
                <View flex={1}>
                  <Text textStyle="md/semibold" fontWeight="600">
                    {feature.title || feature.id}
                  </Text>
                  {feature.description && (
                    <Text color="textMuted" textStyle="sm/regular" mt="xs">
                      {feature.description}
                    </Text>
                  )}
                </View>
                <Switch
                  active={isActive}
                  onToggle={() => handleToggle(feature.id)}
                  size="medium"
                  disabled={isDisabled}
                />
              </View>
            </Pressable>
          );
        })}
      </View>
    </ScrollView>
  );

  return (
    <ScreenContainer edges={[]}>
      <DataStateView
        data={alarm}
        isLoading={isLoadingAlarm}
        isFetching={isFetching}
        error={error}
        refetch={refetch}
        loadingMessage="Loading alarm settings..."
        errorTitle="Alarm not found"
        errorMessage="Could not load alarm response settings."
      >
        {renderResponseContent}
      </DataStateView>
    </ScreenContainer>
  );
}
